import { DatePicker, Tooltip, message } from "antd";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmojiPicker from 'emoji-picker-react';
import moment from "moment";
import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import ProjectServices from "../../../Services/ProjectServices";
import DataParser from "../../../utlits/Dataparser";
import UserServices from "../../../Services/UserService";
import SocketService from "../../../Services/SocketService";
import { FaProjectDiagram } from "react-icons/fa";
import AiTaskReviewModal from "./AiTaskReviewModal";

// Documents the AI can read to suggest tasks
const isAiReadable = (file) => /\.(pdf|docx|txt)$/i.test(file?.name || "");

const CreateProject = (props) => {
    const {
        id,
        labelledby,
        reFreshData,
        closeBtnId,
        onProjectCreated,
    } = props;
    // This component is mounted several times at once (sidebar, Tasks, Projects); label/htmlFor ids
    // must be unique per instance or the paperclip opens another instance's file input.
    const uid = React.useId();
    const attachmentInputId = `project-attachment-${uid}`;
    const aiCheckboxId = `ai-generate-tasks-${uid}`;

    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const quillRef = useRef(null);
    const imageInputRef = useRef(null);
    const insertImageFromFile = (file) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
            const img = new Image();
            img.onload = () => {
                const MAX = 1200;
                const canvas = document.createElement('canvas');
                let w = img.width, h = img.height;
                if (w > MAX || h > MAX) { const r = Math.min(MAX / w, MAX / h); w = Math.round(w * r); h = Math.round(h * r); }
                canvas.width = w; canvas.height = h;
                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                const base64 = canvas.toDataURL('image/jpeg', 0.85);
                const quill = quillRef.current?.getEditor();
                if (!quill) return;
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', base64);
                quill.setSelection(range.index + 1);
            };
            img.src = evt.target.result;
        };
        reader.readAsDataURL(file);
    };
    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const handlePaste = (e) => {
            const imgItem = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith('image/'));
            if (!imgItem) return;
            e.stopPropagation(); e.preventDefault();
            const f = imgItem.getAsFile();
            if (f) insertImageFromFile(f);
        };
        editor.root.addEventListener('paste', handlePaste);
        return () => editor.root.removeEventListener('paste', handlePaste);
    }, []);
    const clickedLinkRef = useRef({ index: null, length: null });
    const linkPopoverRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const [emojiPickerPos, setEmojiPickerPos] = useState({ top: 0, left: 0 });
    const descBlurTimeoutRef = useRef(null);
    const [name, setName] = useState("");
    const [users, setUsers] = useState([]);
    const [owner, setOwner] = useState(null);
    const [status, setStatus] = useState("Planning");
    const [description, setDescription] = useState("");
    const [dueDate, setDueDate] = useState(moment());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [showAllAttachments, setShowAllAttachments] = useState(false);
    // The attached document the AI should turn into tasks (null = AI off), and the review step after creation
    const [aiFile, setAiFile] = useState(null);
    const [aiDetail, setAiDetail] = useState("high"); // high: fewer, bigger tasks | low: small 1-8h steps
    const [aiReview, setAiReview] = useState(null);

    // Description field states
    const [isDescFocused, setIsDescFocused] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const [linkPopoverPos, setLinkPopoverPos] = useState({ top: 0, left: 0 });

    const companyId = DataParser.CompanyId();

    // Modal-style dropdowns
    const [dueDateError, setDueDateError] = useState(false);
    const [isOwnerMenuOpen, setIsOwnerMenuOpen] = useState(false);
    const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
    const [isAiFileMenuOpen, setIsAiFileMenuOpen] = useState(false);
    const [isAiDetailMenuOpen, setIsAiDetailMenuOpen] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState("");

    const filteredUserOptions = users
        .map(user => ({ value: user.id, label: user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() }))
        .filter(option => option.label.toLowerCase().includes(userSearchTerm.toLowerCase()))
        .slice(0, 5);

    useEffect(() => {
        const fetchUsers = () => {
            UserServices.getProjectUsers(companyId)
                .then((res) => {
                    if (res.Good) {
                        setUsers(res.data.user || []);
                    }
                })
                .catch((err) => console.error("Error fetching users:", err));
        };
        fetchUsers();

        const handleClickOutside = (event) => {
            if (!event.target.closest('.owner-select-container')) setIsOwnerMenuOpen(false);
            if (!event.target.closest('.status-select-container')) setIsStatusMenuOpen(false);
            if (!event.target.closest('.ai-file-select-container')) setIsAiFileMenuOpen(false);
            if (!event.target.closest('.ai-detail-select-container')) setIsAiDetailMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [companyId]);

    useEffect(() => {
        if (!showLinkInput) return;
        const handleOutside = (e) => {
            if (linkPopoverRef.current && !linkPopoverRef.current.contains(e.target)) {
                setShowLinkInput(false);
                setLinkUrl('');
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [showLinkInput]);

    useEffect(() => {
        const quill = quillRef.current?.getEditor?.();
        if (!quill) return;
        const isUrl = (s) => /^(https?:\/\/|www\.)\S+$/.test(s);
        const toHref = (s) => /^https?:\/\//i.test(s) ? s : 'https://' + s;
        const handler = (delta, oldDelta, source) => {
            if (source !== 'user') return;
            const last = delta.ops[delta.ops.length - 1];
            if (!last || typeof last.insert !== 'string') return;
            if (!/\s$/.test(last.insert)) return;
            let pos = 0;
            for (const op of delta.ops) {
                if (op.retain) pos += op.retain;
                else if (typeof op.insert === 'string') pos += op.insert.length;
            }
            const text = quill.getText(0, pos);
            const m = text.match(/(\S+)\s+$/);
            if (!m) return;
            const word = m[1];
            if (!isUrl(word)) return;
            const wordEnd = text.trimEnd().length;
            const wordStart = wordEnd - word.length;
            if (quill.getFormat(wordStart, word.length).link) return;
            quill.formatText(wordStart, word.length, 'link', toHref(word), 'api');
        };
        quill.on('text-change', handler);
        return () => quill.off('text-change', handler);
    }, []);

    const resetForm = () => {
        setName("");
        setOwner(null);
        setStatus("Planning");
        setDescription("");
        setDueDate(moment());
        setAttachments([]);
        setAiFile(null);
        setIsSubmitting(false);
        setShowEmojiPicker(false);
        setShowLinkInput(false);
        setDueDateError(false);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachments([...attachments, ...files]);
        e.target.value = null;
    };

    const removeAttachment = (index) => {
        if (attachments[index] === aiFile) setAiFile(null);
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (!name.trim()) {
            message.error("Project name is required");
            return;
        }

        if (!dueDate) {
            setDueDateError(true);
            message.error("Due date is required for a project");
            return;
        }
        setDueDateError(false);

        setIsSubmitting(true);
        const body = {
            name,
            owner: owner ? owner.value : null,
            status,
            description,
            company_id: companyId,
            due_date: dueDate ? dueDate.toISOString() : null,
        };

        try {
            const res = await ProjectServices.createProject(body);
            if (res.Good) {
                const newProjectId = res.data.id;
                let aiAttachmentId = null;

                if (attachments.length > 0) {
                    for (const file of attachments) {
                        const formData = new FormData();
                        formData.append("file", file);
                        const up = await ProjectServices.uploadAttachment(newProjectId, formData, { headers: { "Content-Type": "multipart/form-data" } });
                        if (file === aiFile && up?.Good) aiAttachmentId = up.data.id;
                    }
                }

                message.success("Project created successfully!");
                if (aiAttachmentId) setAiReview({ projectId: newProjectId, attachmentId: aiAttachmentId, projectName: name, detail: aiDetail });
                // Notify sidebar and other tabs about the new project
                SocketService.emit("project_update", { company_id: companyId, action: "create" });
                window.dispatchEvent(new CustomEvent("planit:projectsUpdated", { detail: { company_id: companyId } }));
                const closeBtn = document.getElementById(closeBtnId || `projectClose-${uid}`);
                if (closeBtn) closeBtn.click();
                if (onProjectCreated) onProjectCreated(newProjectId, name);
                if (reFreshData) reFreshData();
                resetForm();
            } else {
                message.error(res.data || "Failed to create project");
            }
        } catch (err) {
            console.error("Project creation failed:", err);
            message.error("An error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    const quillModules = useMemo(() => ({
        toolbar: false,
        keyboard: {
            bindings: {
                autoLink: {
                    key: 32,
                    handler(range) {
                        const q = this.quill;
                        const text = q.getText(0, range.index);
                        const m = text.match(/(\S+)$/);
                        if (m) {
                            const word = m[1];
                            if (/^(https?:\/\/|www\.)\S+$/.test(word)) {
                                const wordStart = range.index - word.length;
                                if (!q.getFormat(wordStart, word.length).link) {
                                    const href = /^https?:\/\//i.test(word) ? word : 'https://' + word;
                                    q.formatText(wordStart, word.length, 'link', href, 'user');
                                }
                            }
                        }
                        return true;
                    }
                }
            }
        }
    }), []);

    const openLinkPopover = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const popW = 320, popH = 60;
        let top = rect.top - popH - 8;
        if (top < 8) top = rect.bottom + 8;
        top = Math.max(8, Math.min(top, window.innerHeight - popH - 8));
        let left = rect.left;
        if (left + popW > window.innerWidth - 8) left = window.innerWidth - popW - 8;
        left = Math.max(8, left);
        const quill = quillRef.current.getEditor();
        const format = quill.getFormat();
        if (format.link) {
            setLinkUrl(format.link);
            const range = quill.getSelection();
            if (range) {
                const [leaf] = quill.getLeaf(range.index);
                let blot = leaf;
                const Quill = ReactQuill.Quill;
                while (blot && blot.statics.blotName !== 'link' && blot !== quill.scroll) { blot = blot.parent; }
                if (blot && blot.statics.blotName === 'link') {
                    clickedLinkRef.current = { index: quill.getIndex(blot), length: blot.length() };
                } else {
                    clickedLinkRef.current = { index: null, length: null };
                }
            }
        } else {
            setLinkUrl("");
            clickedLinkRef.current = { index: null, length: null };
        }
        setLinkPopoverPos({ top, left });
        setShowLinkInput(v => !v);
    };

    const linkPortal = showLinkInput && ReactDOM.createPortal(
        <>
            <div style={{ position: 'fixed', inset: 0, zIndex: 999998 }} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setShowLinkInput(false); setLinkUrl(""); clickedLinkRef.current = { index: null, length: null }; }} />
            <div ref={linkPopoverRef} style={{ position: 'fixed', top: linkPopoverPos.top, left: linkPopoverPos.left, zIndex: 999999, background: '#fff', border: '1.5px solid #1D1D1D', borderRadius: '10px', padding: '4px', width: '320px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }} onMouseDown={e => e.stopPropagation()}>
                <div className="d-flex gap-2 align-items-center">
                    <input type="text" className="link-portal-input" placeholder="Paste or type a link..." style={{ border: 'none', outline: 'none', boxShadow: 'none', fontSize: '14px', padding: '6px 8px', background: 'transparent', flex: 1, minWidth: 0 }} value={linkUrl} onChange={e => setLinkUrl(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                const quill = quillRef.current.getEditor();
                                const { index, length } = clickedLinkRef.current;
                                if (index !== null && length !== null) { quill.formatText(index, length, 'link', linkUrl); } else { quill.format('link', linkUrl); }
                                setShowLinkInput(false); setLinkUrl(""); clickedLinkRef.current = { index: null, length: null };
                            }
                        }}
                    />
                    {linkUrl && (
                        <a href={linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`} target="_blank" rel="noopener noreferrer" className="toolbar-btn d-flex align-items-center justify-content-center" style={{ padding: '4px 8px', borderRadius: '6px', border: '1.5px solid #1D1D1D', color: '#1D1D1D', textDecoration: 'none', background: '#fff' }} title="Open link in new tab">
                            <i className="uil uil-external-link-alt" style={{ fontSize: '18px' }} />
                        </a>
                    )}
                    {linkUrl && (
                        <button type="button" className="toolbar-btn d-flex align-items-center justify-content-center" style={{ padding: '4px 8px', borderRadius: '6px', border: '1.5px solid #ff4d4f', color: '#ff4d4f', background: '#fff' }} title="Remove link"
                            onClick={() => {
                                const quill = quillRef.current.getEditor();
                                const { index, length } = clickedLinkRef.current;
                                if (index !== null && length !== null) { quill.formatText(index, length, 'link', false); } else { quill.format('link', false); }
                                setShowLinkInput(false); setLinkUrl(""); clickedLinkRef.current = { index: null, length: null };
                            }}>
                            <i className="uil uil-link-broken" style={{ fontSize: '18px' }} />
                        </button>
                    )}
                    <button type="button" className="link-apply-btn" onClick={() => {
                        const quill = quillRef.current.getEditor();
                        const { index, length } = clickedLinkRef.current;
                        if (index !== null && length !== null) { quill.formatText(index, length, 'link', linkUrl); } else { quill.format('link', linkUrl); }
                        setShowLinkInput(false); setLinkUrl(""); clickedLinkRef.current = { index: null, length: null };
                    }}>Apply</button>
                </div>
            </div>
        </>,
        document.body
    );

    const openEmojiPicker = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const pickerH = 420, pickerW = 350;
        const spaceBelow = window.innerHeight - rect.bottom - 8;
        const spaceAbove = rect.top - 8;
        let top = spaceBelow >= pickerH || spaceBelow >= spaceAbove
            ? rect.bottom + 8
            : rect.top - pickerH - 8;
        top = Math.max(8, Math.min(top, window.innerHeight - pickerH - 8));
        let left = rect.left;
        if (left + pickerW > window.innerWidth - 8) left = window.innerWidth - pickerW - 8;
        left = Math.max(8, left);
        setEmojiPickerPos({ top, left });
        setShowEmojiPicker(v => !v);
    };

    const emojiPortal = showEmojiPicker && ReactDOM.createPortal(
        <>
            <div
                style={{ position: 'fixed', inset: 0, zIndex: 999998 }}
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setShowEmojiPicker(false); }}
                onClick={e => { e.preventDefault(); e.stopPropagation(); }}
            />
            <div
                ref={emojiPickerRef}
                style={{ position: 'fixed', top: emojiPickerPos.top, left: emojiPickerPos.left, zIndex: 999999, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', borderRadius: 12, overflow: 'hidden' }}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
            >
                <EmojiPicker onEmojiClick={emojiData => {
                    const quill = quillRef.current.getEditor();
                    const range = quill.getSelection();
                    if (range) {
                        if (range.length > 0) quill.deleteText(range.index, range.length);
                        quill.insertText(range.index, emojiData.emoji);
                        quill.setSelection(range.index + emojiData.emoji.length);
                    } else {
                        quill.insertText(quill.getLength(), emojiData.emoji);
                    }
                    setShowEmojiPicker(false);
                }} width={350} height={420} />
            </div>
        </>,
        document.body
    );

    return <>{ReactDOM.createPortal(
        <div
            className="modal fade"
            id={id}
            tabIndex={-1}
            aria-labelledby={labelledby}
            aria-hidden="true"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
        >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "850px" }}>
                <div className="modal-content" style={{ borderRadius: "24px", border: "none", padding: "10px 18px 20px 18px", boxShadow: "0 10px 40px rgba(0,0,0,0.12)" }}>
                    <div className="modal-header border-0 pb-2 d-flex justify-content-end" style={{ paddingBottom: '8px' }}>
                        <button
                            type="button"
                            className="btn-close shadow-none"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            id={closeBtnId || `projectClose-${uid}`}
                            onClick={resetForm}
                            style={{ fontSize: '18px', backgroundSize: '16px', opacity: 0.8 }}
                        />
                    </div>

                    <div className="modal-body pt-0 px-4 pb-1">
                        <style>{`
                .project-title-float-wrap {
                    position: relative;
                    margin-bottom: 8px;
                }
                .project-title-float-wrap input {
                    width: 100%;
                    border-radius: 12px !important;
                    height: 44px !important;
                    padding: 0 16px !important;
                    font-size: 17px !important;
                    font-weight: 500 !important;
                    border: 1.5px solid transparent !important;
                    transition: all 0.2s ease !important;
                    background: transparent;
                    outline: none;
                }
                .project-title-float-wrap input::placeholder {
                    color: #a0a0a0 !important;
                    font-size: 17px !important;
                    font-weight: 500 !important;
                }
                .project-title-float-wrap input:hover, 
                .project-title-float-wrap input:focus { 
                    border: 1.5px solid #1D1D1D !important; 
                    box-shadow: none !important;
                    outline: none !important;
                }
                .project-title-float-wrap label {
                    position: absolute;
                    left: 16px !important;
                    top: 50% !important;
                    transform: translateY(-50%);
                    font-size: 17px !important;
                    opacity: 0 !important; 
                    transition: all 0.2s ease !important;
                    pointer-events: none;
                    background: #fff;
                    padding: 0 5px;
                    color: #1D1D1D;
                    z-index: 10;
                }
                /* Show label ONLY when focused */
                .project-title-float-wrap input:focus ~ label {
                    top: 0 !important;
                    font-size: 12px !important;
                    opacity: 1 !important;
                    transform: translateY(-50%);
                }
                .project-title-float-wrap input:focus::placeholder {
                    color: transparent !important;
                }

                .project-desc-float-wrap {
                    position: relative;
                    border-radius: 12px;
                    border: 1.5px solid transparent;
                    transition: all 0.2s ease;
                    margin-top: 14px;
                    margin-bottom: 12px;
                }
                .project-desc-float-wrap:hover, .project-desc-float-wrap.focused { 
                    border-color: #1D1D1D !important; 
                }

                .project-desc-float-wrap .desc-icon {
                    position: absolute;
                    left: 16px;
                    top: 13px !important;
                    transform: none !important;
                    color: #a0a0a0;
                    transition: all 0.2s ease;
                    pointer-events: none;
                    z-index: 10;
                    opacity: 1;
                }
                .project-desc-float-wrap.focused .desc-icon,
                .project-desc-float-wrap.has-value .desc-icon {
                    opacity: 0 !important;
                }

                .project-desc-float-wrap label {
                    position: absolute;
                    left: 48px;
                    top: 12px !important;
                    transform: none !important;
                    font-size: 17px !important;
                    line-height: 20px !important;
                    color: #a0a0a0;
                    transition: all 0.2s ease !important;
                    pointer-events: none;
                    background: #fff !important;
                    padding: 0 5px;
                    z-index: 99 !important;
                    opacity: 1;
                }
                .project-desc-float-wrap.focused label,
                .project-desc-float-wrap.has-value label {
                    top: 0 !important;
                    transform: translateY(-50%) !important;
                    left: 12px !important;
                    font-size: 12px !important;
                    opacity: 1 !important;
                    color: #1D1D1D !important;
                    background: #fff !important;
                    z-index: 99 !important;
                }
                .project-desc-float-wrap.has-value:not(.focused) label {
                    opacity: 0 !important;
                    visibility: hidden !important;
                    pointer-events: none !important;
                }

                .ql-editor {
                    padding: 12px 16px 8px 48px !important;
                    min-height: 44px !important;
                    max-height: 200px !important;
                    overflow-y: auto !important;
                    font-size: 15px !important;
                    transition: all 0.2s ease;
                }
                .project-desc-float-wrap.focused .ql-editor,
                .project-desc-float-wrap.has-value .ql-editor,
                .project-desc-float-wrap .ql-editor:not(.ql-blank) {
                    padding-left: 16px !important;
                }
                .ql-editor.ql-blank::before {
                    display: none !important;
                }
                .ql-container.ql-snow {
                    border: none !important;
                    font-family: inherit !important;
                }

                .desc-toolbar {
                    display: flex;
                    gap: 4px;
                    background: #F5F5F5;
                    padding: 2px 4px;
                    border-radius: 6px;
                    width: fit-content;
                    margin: 0 0 6px 16px;
                    opacity: 0;
                    visibility: hidden;
                    pointer-events: none !important;
                    transition: all 0.2s ease;
                }
                .project-desc-float-wrap.focused .desc-toolbar {
                    opacity: 1;
                    visibility: visible;
                    pointer-events: auto !important;
                }
                .toolbar-btn {
                    background: transparent !important;
                    border: none !important;
                    padding: 0 !important;
                    border-radius: 4px;
                    color: #1D1D1D !important;
                    font-size: 14px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    width: 26px !important;
                    height: 26px !important;
                }
                .toolbar-btn i {
                    font-size: 14px !important;
                }
                .toolbar-btn:hover {
                    background: #E8E8E8 !important;
                }

                .action-btn-bar {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-bottom: 24px;
                }
                .action-btn-wrap {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 4px 12px;
                    background: #fff;
                    border: 1px solid #E8E8E8;
                    border-radius: 6px;
                    min-height: 30px;
                    height: 30px;
                    transition: all 0.2s;
                    user-select: none;
                    color: #A0A0A0;
                }
                .action-btn-wrap:hover, .action-btn-wrap.active {
                    border-color: #1D1D1D !important;
                    color: #1D1D1D !important;
                    background: #fff;
                }
                .action-btn-wrap:hover .action-icon, .action-btn-wrap.active .action-icon {
                    color: #1D1D1D !important;
                }
                .action-icon {
                    font-size: 14px;
                    color: inherit !important;
                    display: flex;
                    align-items: center;
                }

                .custom-user-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    width: 250px;
                    background: #fff;
                    border: 1px solid #E8E8E8;
                    border-radius: 10px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    z-index: 10000;
                    margin-top: 5px;
                    overflow: hidden;
                }
                .search-header {
                    padding: 12px 16px;
                    border-bottom: 1px solid #F0F0F0;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .search-header i {
                    font-size: 18px;
                    color: #1a1a1a;
                }
                .search-header input {
                    border: none !important;
                    outline: none !important;
                    box-shadow: none !important;
                    font-size: 14px;
                    width: 100%;
                    color: #1D1D1D;
                    font-weight: 400;
                }
                .user-list-scroll {
                    padding: 5px;
                    max-height: 175px !important;
                    overflow-y: auto;
                }
                .user-option-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 8px 10px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-size: 11px;
                    color: #1a1a1a;
                }
                .user-option-item:hover {
                    background: #f0fdf9;
                }
                .user-avatar-mini {
                    width: 20px;
                    height: 20px;
                    background-color: #0DDFAB;
                    color: #000;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 9px;
                    font-weight: 700;
                }

                .attachment-chip {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 12px;
                    background: #1D1D1D;
                    color: #0DDFAB;
                    border-radius: 5px;
                    font-size: 12px;
                    font-weight: 600;
                }
                
                .action-datepicker .ant-picker-input > input {
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    color: #1D1D1D !important;
                    cursor: pointer !important;
                }
                .action-datepicker .ant-picker-input > input::placeholder {
                    color: #A0A0A0 !important;
                }

                .link-popover {
                    background: #fff;
                    border: 1.5px solid #1D1D1D;
                    border-radius: 10px;
                    padding: 4px;
                    bottom: 50px !important;
                    left: 10px !important;
                    width: 320px;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                    z-index: 10001;
                }
                .link-popover input { border: none !important; box-shadow: none !important; outline: none !important; font-size: 14px !important; padding: 8px 12px !important; }
                .link-popover input:focus { border: none !important; border-color: transparent !important; box-shadow: none !important; outline: none !important; }
                .link-apply-btn { background-color: #0DDFAB !important; border: none !important; color: #000 !important; font-weight: 600 !important; border-radius: 8px !important; padding: 6px 16px !important; font-size: 13px !important; cursor: pointer; }

                /* Custom Dropdown Panel for Status */
                .custom-dropdown-panel {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    width: 180px;
                    background: #fff;
                    border-radius: 12px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                    border: 1px solid #E8E8E8;
                    margin-top: 8px;
                    z-index: 1000;
                    padding: 8px;
                }
                .option-list {
                    max-height: 200px;
                    overflow-y: auto;
                }
                .option-item {
                    padding: 8px 12px;
                    border-radius: 6px;
                    font-size: 11px;
                    color: #1D1D1D;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .option-item:hover { background: #f0fdf9; }
                .custom-switch:checked {
                    background-color: #0DDFAB !important;
                    border-color: #0DDFAB !important;
                }
                .attachment-trigger {
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff;
                    border-radius: 6px;
                    transition: all 0.2s ease;
                    border: 1px solid #E8E8E8;
                }
                .attachment-trigger:hover { background: #f0f1f2; border-color: #adb5bd; }

                .btn-create-project {
                    background-color: #0DDFAB;
                    border: none;
                    border-radius: 5px;
                    height: 30px;
                    padding: 0 20px;
                    color: #000;
                    font-weight: 700;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    box-shadow: none !important;
                    flex-shrink: 0 !important;
                }
                .btn-create-project:hover {
                    background-color: #0bc698;
                    transform: translateY(-2px);
                }
                .btn-create-project:active { transform: translateY(0); }
                .btn-create-project:disabled { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }

                .attachment-preview-chip {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: #1D1D1D;
                    color: #0DDFAB;
                    padding: 0 8px;
                    height: 24px;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                }
                .chip-file-name {
                    max-width: 150px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .chip-remove-icon {
                    font-size: 12px;
                    color: #0DDFAB;
                    cursor: pointer;
                    transition: transform 0.2s;
                }
            `}</style>

                        <form onSubmit={handleSubmit}>
                            <div className="project-title-float-wrap">
                                <input
                                    type="text"
                                    id="projectTitleInput"
                                    placeholder="Project Name"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                    }}
                                    autoFocus
                                />
                                <label htmlFor="projectTitleInput">Project Name</label>
                            </div>

                            {/* Description Section */}
                            <div
                                className={`project-desc-float-wrap ${isDescFocused || showLinkInput || showEmojiPicker ? 'focused' : ''} ${(description && description !== '<p><br></p>') ? 'has-value' : ''}`}
                                onClick={(e) => {
                                    const anchor = e.target.closest('a');
                                    if (anchor && anchor.closest('.ql-editor')) {
                                        e.preventDefault();
                                        const url = anchor.getAttribute('href');
                                        setLinkUrl(url);
                                        setShowLinkInput(true);

                                        const q = quillRef.current.getEditor();
                                        const Quill = ReactQuill.Quill;
                                        const blot = Quill.find(anchor);
                                        if (blot) {
                                            clickedLinkRef.current = {
                                                index: q.getIndex(blot),
                                                length: blot.length()
                                            };
                                        }
                                    }
                                }}
                            >
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    placeholder=""
                                    value={description}
                                    onChange={setDescription}
                                    onFocus={() => {
                                        if (descBlurTimeoutRef.current) {
                                            clearTimeout(descBlurTimeoutRef.current);
                                            descBlurTimeoutRef.current = null;
                                        }
                                        setIsDescFocused(true);
                                    }}
                                    onBlur={() => {
                                        descBlurTimeoutRef.current = setTimeout(() => {
                                            setIsDescFocused(false);
                                        }, 150);
                                    }}
                                    modules={quillModules}
                                />
                                <span className="desc-icon" style={{ display:'inline-block', width:'18px', height:'18px', flexShrink:0, backgroundColor: '#a0a0a0', mask:"url('/assets/images/descriptionmodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/descriptionmodal.svg') no-repeat center / contain" }} />
                                <label>Description</label>

                                {/* Custom Toolbar */}
                                <div className="desc-toolbar" onMouseDown={(e) => e.preventDefault()}>
                                    <button type="button" className="toolbar-btn" onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const format = quill.getFormat();
                                        quill.format('bold', !format.bold);
                                    }}>
                                        <i className="uil uil-bold" />
                                    </button>
                                    <button type="button" className="toolbar-btn" onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const format = quill.getFormat();
                                        quill.format('italic', !format.italic);
                                    }}>
                                        <i className="uil uil-italic" />
                                    </button>
                                    <button type="button" className="toolbar-btn" onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const format = quill.getFormat();
                                        quill.format('underline', !format.underline);
                                    }}>
                                        <i className="uil uil-underline" />
                                    </button>
                                    <button type="button" className="toolbar-btn" onClick={openEmojiPicker}>
                                        <i className="uil uil-smile" />
                                    </button>
                                    <button type="button" className="toolbar-btn" onClick={openLinkPopover}>
                                        <i className="uil uil-link" />
                                    </button>
                                    <button type="button" className="toolbar-btn" onClick={() => {
                                        const quill = quillRef.current.getEditor();
                                        const format = quill.getFormat();
                                        quill.format('list', format.list === 'bullet' ? false : 'bullet');
                                    }}>
                                        <i className="uil uil-list-ul" />
                                    </button>
                                    <button type="button" className="toolbar-btn" title="Insert image" onClick={() => imageInputRef.current?.click()}>
                                        <i className="uil uil-image" />
                                    </button>
                                    <input type="file" ref={imageInputRef} hidden accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) { insertImageFromFile(f); e.target.value = ''; } }} />
                                </div>
                            </div>

                            <div className="action-btn-bar mt-2">
                                {/* Owner Select */}
                                <div className="owner-select-container position-relative">
                                    <Tooltip title="Assign Owner" placement="top">
                                        <div
                                            className={`action-btn-wrap cursor-pointer ${isOwnerMenuOpen || owner ? 'active' : ''}`}
                                            onClick={() => setIsOwnerMenuOpen(!isOwnerMenuOpen)}
                                        >
                                            <span style={{ display:'inline-block', width:'15px', height:'15px', flexShrink:0, backgroundColor: 'currentColor', mask:"url('/assets/images/assignedtomodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/assignedtomodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                            <span style={{ fontSize: '11px', fontWeight: '600' }}>
                                                {owner ? owner.label : "Assign Owner"}
                                            </span>
                                            <i className={`uil uil-angle-down ms-1`} style={{ fontSize: '14px', transition: 'transform 0.2s', transform: isOwnerMenuOpen ? 'rotate(180deg)' : 'none' }} />
                                        </div>
                                    </Tooltip>

                                    {isOwnerMenuOpen && (
                                        <div className="custom-user-dropdown">
                                            <div className="search-header">
                                                <i className="uil uil-search" />
                                                <input
                                                    placeholder="Search or enter email..."
                                                    value={userSearchTerm}
                                                    onChange={(e) => setUserSearchTerm(e.target.value)}
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="user-list-scroll custom-comment-scroll">
                                                {filteredUserOptions.length > 0 ? filteredUserOptions.map(opt => (
                                                    <div
                                                        key={opt.value}
                                                        className="user-option-item"
                                                        onClick={() => {
                                                            setOwner(opt);
                                                            setIsOwnerMenuOpen(false);
                                                            setUserSearchTerm("");
                                                        }}
                                                    >
                                                        <div className="user-avatar-mini">
                                                            {opt.label.charAt(0)}
                                                        </div>
                                                        <span>{opt.label}</span>
                                                    </div>
                                                )) : <div className="p-3 text-muted text-center" style={{ fontSize: '12px' }}>No members found</div>}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Status Select */}
                                <div className="status-select-container position-relative">
                                    <Tooltip title="Project Status" placement="top">
                                        <div
                                            className={`action-btn-wrap cursor-pointer active`}
                                            onClick={() => setIsStatusMenuOpen(!isStatusMenuOpen)}
                                        >
                                            <FaProjectDiagram className="action-icon" />
                                            <span style={{ fontSize: '11px', fontWeight: '600' }}>
                                                {status}
                                            </span>
                                            <i className={`uil uil-angle-down ms-1`} style={{ fontSize: '14px', transition: 'transform 0.2s', transform: isStatusMenuOpen ? 'rotate(180deg)' : 'none' }} />
                                        </div>
                                    </Tooltip>

                                    {isStatusMenuOpen && (
                                        <div className="custom-dropdown-panel" style={{ width: '180px' }}>
                                            <div className="option-list">
                                                {["Planning", "In Progress", "On Hold", "Completed", "Abandoned"].map(s => (
                                                    <div
                                                        key={s}
                                                        className="option-item"
                                                        onClick={() => {
                                                            setStatus(s);
                                                            setIsStatusMenuOpen(false);
                                                        }}
                                                    >
                                                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: s === 'Planning' ? '#3498db' : s === 'In Progress' ? '#f39c12' : s === 'Completed' ? '#27ae60' : s === 'On Hold' ? '#e74c3c' : '#95a5a6' }} />
                                                        {s}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Due Date Select */}
                                <Tooltip title="Due Date" placement="top">
                                    <div className={`action-btn-wrap position-relative ${dueDate ? 'active' : ''}`} style={{ borderColor: dueDateError ? '#ff4d4f' : undefined }}>
                                        <span style={{ display:'inline-block', width:'15px', height:'15px', flexShrink:0, backgroundColor: dueDateError ? '#ff4d4f' : 'currentColor', mask:"url('/assets/images/duedatemodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/duedatemodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                        <DatePicker
                                            value={dueDate}
                                            onChange={(date) => { setDueDate(date); if (date) setDueDateError(false); }}
                                            format="DD MMM"
                                            placeholder="Set Due Date"
                                            className="action-datepicker"
                                            bordered={false}
                                            allowClear={false}
                                            suffixIcon={null}
                                            style={{ padding: 0, width: '90px' }}
                                        />
                                    </div>
                                </Tooltip>

                            </div>

                            {/* AI: turn an attached scope document into suggested tasks (opt-in, reviewed before saving) */}
                            {/* Until a readable document is attached: tell the user AI can plan the tasks from one */}
                            {!attachments.some(isAiReadable) && (
                                <div className="d-flex align-items-center gap-2" style={{ fontSize: '12px', color: '#A0A0A0', margin: '0 0 12px 0' }}>
                                    <i className="uil uil-lightbulb-alt" style={{ fontSize: '16px', color: '#A0A0A0' }} />
                                    <span>
                                        Have a scope document? Attach a <span style={{ color: '#1D1D1D', fontWeight: 600 }}>PDF, DOCX or TXT</span> file and AI will generate this project's tasks for you to review.
                                    </span>
                                </div>
                            )}

                            {attachments.some(isAiReadable) && (
                                <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="form-check form-switch m-0">
                                            <input
                                                className="form-check-input custom-switch"
                                                type="checkbox"
                                                id={aiCheckboxId}
                                                checked={!!aiFile}
                                                onChange={(e) => setAiFile(e.target.checked ? attachments.find(isAiReadable) : null)}
                                            />
                                        </div>
                                        <label htmlFor={aiCheckboxId} style={{ fontSize: '13px', fontWeight: '600', color: '#1D1D1D', margin: 0, cursor: 'pointer' }}>Generate tasks with AI</label>
                                    </div>

                                    {aiFile && (
                                        <>
                                            {/* Which attached document the AI reads */}
                                            <div className="ai-file-select-container position-relative">
                                                <Tooltip title="Document for AI" placement="top">
                                                    <div
                                                        className={`action-btn-wrap active ${attachments.filter(isAiReadable).length > 1 ? 'cursor-pointer' : ''}`}
                                                        onClick={() => attachments.filter(isAiReadable).length > 1 && setIsAiFileMenuOpen(!isAiFileMenuOpen)}
                                                    >
                                                        <i className="uil uil-file-alt action-icon" />
                                                        <span className="text-truncate" style={{ fontSize: '11px', fontWeight: '600', maxWidth: '200px' }}>{aiFile.name}</span>
                                                        {attachments.filter(isAiReadable).length > 1 && (
                                                            <i className={`uil uil-angle-down ms-1`} style={{ fontSize: '14px', transition: 'transform 0.2s', transform: isAiFileMenuOpen ? 'rotate(180deg)' : 'none' }} />
                                                        )}
                                                    </div>
                                                </Tooltip>
                                                {isAiFileMenuOpen && (
                                                    <div className="custom-dropdown-panel" style={{ width: '260px' }}>
                                                        <div className="option-list">
                                                            {attachments.filter(isAiReadable).map((f, i) => (
                                                                <div
                                                                    key={i}
                                                                    className="option-item"
                                                                    style={{ fontWeight: f === aiFile ? '600' : '400' }}
                                                                    onClick={() => { setAiFile(f); setIsAiFileMenuOpen(false); }}
                                                                >
                                                                    <i className="uil uil-file-alt" />
                                                                    <span className="text-truncate">{f.name}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* How detailed the generated tasks are */}
                                            <div className="ai-detail-select-container position-relative">
                                                <Tooltip title="Task Detail" placement="top">
                                                    <div className="action-btn-wrap cursor-pointer active" onClick={() => setIsAiDetailMenuOpen(!isAiDetailMenuOpen)}>
                                                        <i className="uil uil-list-ul action-icon" />
                                                        <span style={{ fontSize: '11px', fontWeight: '600' }}>
                                                            {aiDetail === 'low' ? 'Low-level tasks' : 'High-level tasks'}
                                                        </span>
                                                        <i className={`uil uil-angle-down ms-1`} style={{ fontSize: '14px', transition: 'transform 0.2s', transform: isAiDetailMenuOpen ? 'rotate(180deg)' : 'none' }} />
                                                    </div>
                                                </Tooltip>
                                                {isAiDetailMenuOpen && (
                                                    <div className="custom-dropdown-panel" style={{ width: '220px' }}>
                                                        <div className="option-list">
                                                            {[
                                                                { value: 'high', label: 'High-level tasks', hint: 'Fewer, bigger deliverables' },
                                                                { value: 'low', label: 'Low-level tasks', hint: 'Detailed steps, 1–8h each' },
                                                            ].map(o => (
                                                                <div
                                                                    key={o.value}
                                                                    className="option-item"
                                                                    style={{ fontWeight: aiDetail === o.value ? '600' : '400' }}
                                                                    onClick={() => { setAiDetail(o.value); setIsAiDetailMenuOpen(false); }}
                                                                >
                                                                    <div>
                                                                        <div>{o.label}</div>
                                                                        <div style={{ color: '#A0A0A0', fontWeight: '400', fontSize: '10px' }}>{o.hint}</div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <span style={{ color: '#A0A0A0', fontSize: '11px' }}>The document is sent to Google Gemini. You review the tasks before they're created.</span>
                                </div>
                            )}

                            {/* Footer Actions */}
                            <div className="col-12 d-flex align-items-center justify-content-between mt-2 pt-2 border-top">
                                <div className="d-flex align-items-center gap-3">
                                    <Tooltip title="Attach Files" placement="top">
                                        <label htmlFor={attachmentInputId} style={{ cursor: 'pointer', margin: 0 }}>
                                            <div className="attachment-trigger">
                                                <span style={{ display:'inline-block', width:'16px', height:'16px', flexShrink:0, backgroundColor: '#1a1a1a', mask:"url('/assets/images/attachmentmodal.svg') no-repeat center / contain", WebkitMask:"url('/assets/images/attachmentmodal.svg') no-repeat center / contain", transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', imageRendering: '-webkit-optimize-contrast' }} />
                                            </div>
                                        </label>
                                    </Tooltip>
                                    <input
                                        type="file"
                                        id={attachmentInputId}
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        multiple
                                        onChange={handleFileChange}
                                    />

                                    {attachments.length > 0 && (
                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                            {(showAllAttachments ? attachments : attachments.slice(0, 3)).map((file, idx) => (
                                                <div key={idx} className="attachment-preview-chip">
                                                    <i className="uil uil-file-alt" />
                                                    <span className="chip-file-name">{file.name}</span>
                                                    <i className="uil uil-times chip-remove-icon ms-1" onClick={() => removeAttachment(idx)} />
                                                </div>
                                            ))}
                                            {attachments.length > 3 && (
                                                <div
                                                    className="attachment-preview-chip cursor-pointer"
                                                    onClick={() => setShowAllAttachments(!showAllAttachments)}
                                                    style={{ background: '#f8f9fa', border: '1px dashed #adb5bd', color: '#1D1D1D' }}
                                                >
                                                    <span style={{ fontWeight: 600 }}>
                                                        {showAllAttachments ? "Show Less" : `+ ${attachments.length - 3} more`}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="btn-create-project"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <div className="spinner-border spinner-border-sm me-2" role="status" />
                                    ) : (
                                        <i className="uil uil-plus me-1" style={{ fontSize: '16px' }} />
                                    )}
                                    Create Project
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )}{emojiPortal}{linkPortal}
    {aiReview && <AiTaskReviewModal {...aiReview} onClose={() => setAiReview(null)} />}</>;
};

export default CreateProject;
